/**
 * Sketch-mode pointer event handler.
 *
 * Captures the user's drag as a sequence of stroke points. The sketch mode's
 * `projection` field decides what each point means:
 *
 *  - "projected"   — Raycast every sketchable mesh in the scene for each
 *                    sample. Each projected point stores the host object it
 *                    hit, so a single stroke can flow across multiple
 *                    surfaces. If the cursor leaves all surfaces, the sample
 *                    falls onto a plane locked at pointer-down and attaches
 *                    to the last sampled surface.
 *  - "screenspace" — Each sample is placed 1 unit from the camera along
 *                    the cursor ray and stored in world space. Anchored on
 *                    creation; orbiting the camera does not move strokes.
 *
 * Pen input is always accepted; mouse/touch require the
 * `allowNonPenDrawingInput` toggle.
 *
 * Performance: pointermove events are coalesced via requestAnimationFrame so
 * we run at most one raycast per frame regardless of device event rate.
 */

import { useThree } from "@react-three/fiber";
import { useCallback, useEffect, useRef } from "react";
import * as THREE from "three/webgpu";
import { useStore } from "../app/store.ts";
import type { SketchStrokePointInput } from "../app/storeTypes.ts";
import { isProjectedStroke } from "../modeling/sketch.ts";
import { isImagePlaneObject, isLightObject } from "../modeling/types.ts";
import {
  type ScreenPoint,
  sampleScreenLine,
  snapScreenPointTo45,
} from "../sketch/sketchLineMath.ts";
import {
  bakeSkinAttachment,
  distance3,
  findStrokesUnderCursor,
  hitToBindPose,
  projectedStrokeWorldPositions,
  rayPlaneIntersect,
} from "../sketch/sceneSketch.ts";
import type { BrushCursorState } from "./BrushCursor.tsx";
import {
  screenToObjectLocalRadius,
  screenToWorldRadius,
} from "./screenRadius.ts";
import {
  buildSketchMeshIndex,
  collectSketchRaycastTargets,
  sketchTargetScore,
} from "./sketchTargets.ts";

/** Distance (world units) at which screenspace strokes are placed in front
 *  of the camera. Industry-standard "1 unit out" — matches Blender Grease
 *  Pencil's screenspace stroke origin. */
const SCREENSPACE_DEPTH = 1.0;

/** Minimum mesh-local distance between successive projected stroke points,
 *  expressed as a fraction of the object-local tool thickness. */
const MIN_POINT_SPACING_FRACTION = 0.25;

/** Minimum world-space distance between successive screenspace stroke
 *  points, in world units. Prevents over-sampling in screenspace mode where
 *  there's no per-mesh local radius to lean on. */
const SCREENSPACE_MIN_SPACING = 0.005;

/** Screen-space distance between ruler samples. Projected ruler strokes raycast
 *  every sample, so this is deliberately coarser than freehand pointer input. */
const LINE_SAMPLE_SPACING_PX = 10;

/** Caps live ruler regeneration cost on large canvases / long drags. */
const LINE_MAX_SEGMENTS = 96;

function strokeDebugEnabled(): boolean {
  return (
    (globalThis as { __STROKE_DEBUG__?: boolean }).__STROKE_DEBUG__ === true
  );
}

function logSketchDebug(msg: string, data?: unknown): void {
  if (!strokeDebugEnabled()) return;
  console.log(`[sketch] ${msg}`, data ?? "");
}

function logSketchWarn(msg: string, data?: unknown): void {
  if (!strokeDebugEnabled()) return;
  console.warn(`[sketch] ${msg}`, data ?? "");
}

function finitePoint(p: [number, number, number]): boolean {
  return (
    Number.isFinite(p[0]) && Number.isFinite(p[1]) && Number.isFinite(p[2])
  );
}

/** True when a SceneObject (resolved by id) accepts projected sketch. */
function isObjectSketchable(objectId: string): boolean {
  const state = useStore.getState();
  const obj = state.sceneObjects.get(objectId);
  if (!obj) return false;
  if (isLightObject(obj)) return false;
  if (isImagePlaneObject(obj)) return false;
  return true;
}

const _skinnedRayInvMat = new THREE.Matrix4();
const _skinnedRayLocal = new THREE.Ray();
const _skinnedRayA = new THREE.Vector3();
const _skinnedRayB = new THREE.Vector3();
const _skinnedRayC = new THREE.Vector3();
const _skinnedRayHitLocal = new THREE.Vector3();
const _skinnedRayHitWorld = new THREE.Vector3();
const _skinnedRayBestPoint = new THREE.Vector3();
const _skinnedRayNormal = new THREE.Vector3();
const _skinnedRayBestNormal = new THREE.Vector3();
const _skinnedRayBary = new THREE.Vector3();
const _skinnedRayBestBary = new THREE.Vector3();
const _skinnedSketchVertex = new THREE.Vector3();

interface SkinnedSketchVertexCache {
  geometry: THREE.BufferGeometry;
  positions: Float32Array;
  vertexCount: number;
}

const _skinnedSketchVertexCache = new WeakMap<
  THREE.SkinnedMesh,
  SkinnedSketchVertexCache
>();

function withStashedMorphInfluences<T>(
  mesh: THREE.SkinnedMesh,
  fn: () => T,
): T {
  const stashed = mesh.userData?.stashedInfluences;
  if (!Array.isArray(stashed) || stashed.length === 0) return fn();
  const previous = mesh.morphTargetInfluences;
  mesh.morphTargetInfluences = stashed as number[];
  try {
    return fn();
  } finally {
    mesh.morphTargetInfluences = previous;
  }
}

function getSkinnedSketchVertexPositions(
  mesh: THREE.SkinnedMesh,
  vertexCount: number,
): Float32Array {
  let cache = _skinnedSketchVertexCache.get(mesh);
  if (
    !cache ||
    cache.geometry !== mesh.geometry ||
    cache.vertexCount !== vertexCount
  ) {
    cache = {
      geometry: mesh.geometry,
      positions: new Float32Array(vertexCount * 3),
      vertexCount,
    };
    _skinnedSketchVertexCache.set(mesh, cache);
  }

  const positions = cache.positions;
  withStashedMorphInfluences(mesh, () => {
    for (let i = 0; i < vertexCount; i++) {
      mesh.getVertexPosition(i, _skinnedSketchVertex);
      const o = i * 3;
      positions[o] = _skinnedSketchVertex.x;
      positions[o + 1] = _skinnedSketchVertex.y;
      positions[o + 2] = _skinnedSketchVertex.z;
    }
  });
  return positions;
}

function raycastSkinnedMeshForSketch(
  mesh: THREE.SkinnedMesh,
  raycaster: THREE.Raycaster,
  skinnedPositionsOverride?: Float32Array,
): THREE.Intersection | null {
  const geometry = mesh.geometry;
  const position = geometry.getAttribute("position");
  if (!position) return null;

  _skinnedRayInvMat.copy(mesh.matrixWorld).invert();
  _skinnedRayLocal.copy(raycaster.ray).applyMatrix4(_skinnedRayInvMat);

  const index = geometry.getIndex();
  const triCount = index
    ? Math.floor(index.count / 3)
    : Math.floor(position.count / 3);
  const skinnedPositions =
    skinnedPositionsOverride ??
    getSkinnedSketchVertexPositions(mesh, position.count);
  let bestDistance = Number.POSITIVE_INFINITY;
  let bestFaceIndex = -1;
  let bestA = -1;
  let bestB = -1;
  let bestC = -1;

  for (let tri = 0; tri < triCount; tri++) {
    const i3 = tri * 3;
    const a = index ? index.getX(i3) : i3;
    const b = index ? index.getX(i3 + 1) : i3 + 1;
    const c = index ? index.getX(i3 + 2) : i3 + 2;
    _skinnedRayA.fromArray(skinnedPositions, a * 3);
    _skinnedRayB.fromArray(skinnedPositions, b * 3);
    _skinnedRayC.fromArray(skinnedPositions, c * 3);
    const localHit = _skinnedRayLocal.intersectTriangle(
      _skinnedRayA,
      _skinnedRayB,
      _skinnedRayC,
      false,
      _skinnedRayHitLocal,
    );
    if (!localHit) continue;

    _skinnedRayHitWorld.copy(localHit).applyMatrix4(mesh.matrixWorld);
    const distance = raycaster.ray.origin.distanceTo(_skinnedRayHitWorld);
    if (
      distance < raycaster.near ||
      distance > raycaster.far ||
      distance >= bestDistance
    ) {
      continue;
    }

    THREE.Triangle.getNormal(
      _skinnedRayA,
      _skinnedRayB,
      _skinnedRayC,
      _skinnedRayNormal,
    );
    THREE.Triangle.getBarycoord(
      localHit,
      _skinnedRayA,
      _skinnedRayB,
      _skinnedRayC,
      _skinnedRayBary,
    );
    if (
      !Number.isFinite(_skinnedRayBary.x) ||
      !Number.isFinite(_skinnedRayBary.y) ||
      !Number.isFinite(_skinnedRayBary.z)
    ) {
      continue;
    }

    bestDistance = distance;
    bestFaceIndex = tri;
    bestA = a;
    bestB = b;
    bestC = c;
    _skinnedRayBestPoint.copy(_skinnedRayHitWorld);
    _skinnedRayBestNormal.copy(_skinnedRayNormal);
    _skinnedRayBestBary.copy(_skinnedRayBary);
  }

  if (bestFaceIndex < 0) return null;
  return {
    distance: bestDistance,
    point: _skinnedRayBestPoint.clone(),
    object: mesh,
    face: {
      a: bestA,
      b: bestB,
      c: bestC,
      normal: _skinnedRayBestNormal.clone(),
      materialIndex: 0,
    },
    faceIndex: bestFaceIndex,
    normal: _skinnedRayBestNormal.clone(),
    barycoord: _skinnedRayBestBary.clone(),
  };
}

interface ProjectedSurfaceSample {
  mesh: THREE.Mesh;
  objectId: string;
  topologyVersion: number;
  hit: THREE.Intersection;
}

/** Projected stroke context captured while a projected stroke is active. */
interface ProjectedStrokeContext {
  lastSurface: Omit<ProjectedSurfaceSample, "hit">;
  fallbackPlane: {
    point: [number, number, number];
    normal: [number, number, number];
  };
}

interface SketchPointerInput {
  clientX: number;
  clientY: number;
  pointerType: string;
  pressure: number;
  shiftKey: boolean;
}

interface CapturedSketchPoint extends SketchStrokePointInput {
  mesh: THREE.Mesh | null;
  worldHit: THREE.Vector3 | null;
  worldNormal: THREE.Vector3 | null;
}

interface RulerStrokeContext {
  kind: "projected" | "screenspace";
  startScreen: ScreenPoint;
  startPoint: SketchStrokePointInput;
}

function eventToSketchInput(e: PointerEvent): SketchPointerInput {
  return {
    clientX: e.clientX,
    clientY: e.clientY,
    pointerType: e.pointerType,
    pressure: e.pressure,
    shiftKey: e.shiftKey,
  };
}

function pressureFromInput(
  input: Pick<SketchPointerInput, "pointerType" | "pressure">,
) {
  return input.pointerType === "pen" ? input.pressure : 0.5;
}

export function SketchHandler({
  cursorRef,
}: {
  cursorRef: React.MutableRefObject<BrushCursorState>;
}) {
  const { gl, scene, camera, size: viewportSize } = useThree();
  const strokeActive = useRef(false);
  const raycaster = useRef(new THREE.Raycaster());
  const lastProjectedWorldPoint = useRef<[number, number, number] | null>(null);
  const lastScreenspacePoint = useRef<[number, number, number] | null>(null);
  /** Set on pointerdown for projected strokes; null in screenspace mode. */
  const projectedCtx = useRef<ProjectedStrokeContext | null>(null);
  const rulerCtx = useRef<RulerStrokeContext | null>(null);
  const rulerActive = useRef(false);

  const screenToNDC = useCallback(
    (screenX: number, screenY: number) => {
      const canvas = gl.domElement;
      const rect = canvas.getBoundingClientRect();
      const ndc = new THREE.Vector2(
        ((screenX - rect.left) / rect.width) * 2 - 1,
        -((screenY - rect.top) / rect.height) * 2 + 1,
      );
      return { ndc, rect };
    },
    [gl],
  );

  /** Find the best sketchable mesh under the cursor. */
  const pickProjectedSurface = useCallback(
    (
      screenX: number,
      screenY: number,
    ): ProjectedSurfaceSample | null => {
      const { ndc } = screenToNDC(screenX, screenY);
      raycaster.current.setFromCamera(ndc, camera);

      const candidates = collectSketchRaycastTargets(scene, isObjectSketchable);

      // Test each candidate, keep the closest hit (tie-broken by score).
      let best: {
        mesh: THREE.Mesh;
        objectId: string;
        hit: THREE.Intersection;
        score: number;
      } | null = null;
      for (const { mesh, objectId } of candidates) {
        const skinnedMesh = (mesh as THREE.SkinnedMesh).isSkinnedMesh
          ? (mesh as THREE.SkinnedMesh)
          : null;
        let hit: THREE.Intersection | null;
        if (skinnedMesh?.skeleton) {
          mesh.updateWorldMatrix(true, false);
          skinnedMesh.skeleton.update();
          hit = raycastSkinnedMeshForSketch(skinnedMesh, raycaster.current);
        } else {
          const hits = raycaster.current.intersectObject(mesh, false);
          hit = hits.length > 0 ? hits[0] : null;
        }
        if (!hit) continue;
        const score = sketchTargetScore(mesh);
        if (
          !best ||
          hit.distance < best.hit.distance - 1e-6 ||
          (Math.abs(hit.distance - best.hit.distance) < 1e-6 &&
            score > best.score)
        ) {
          best = { mesh, objectId, hit, score };
        }
      }
      if (!best) return null;
      const obj = useStore.getState().sceneObjects.get(best.objectId);
      if (!obj) return null;
      return {
        mesh: best.mesh,
        objectId: best.objectId,
        topologyVersion: obj.topologyVersion ?? 0,
        hit: best.hit,
      };
    },
    [camera, scene, screenToNDC],
  );

  /** Build the camera ray's screenspace placement point, 1 unit out. */
  const buildScreenspacePoint = useCallback(
    (screenX: number, screenY: number): [number, number, number] => {
      const { ndc } = screenToNDC(screenX, screenY);
      raycaster.current.setFromCamera(ndc, camera);
      const o = raycaster.current.ray.origin;
      const d = raycaster.current.ray.direction;
      return [
        o.x + d.x * SCREENSPACE_DEPTH,
        o.y + d.y * SCREENSPACE_DEPTH,
        o.z + d.z * SCREENSPACE_DEPTH,
      ];
    },
    [camera, screenToNDC],
  );

  /** Project the current cursor ray onto the locked fallback plane. Used
   *  when a projected stroke's cursor leaves all sketchable surfaces. */
  const projectOntoFallbackPlane = useCallback(
    (
      screenX: number,
      screenY: number,
      ctx: ProjectedStrokeContext,
    ): THREE.Vector3 | null => {
      const { ndc } = screenToNDC(screenX, screenY);
      raycaster.current.setFromCamera(ndc, camera);
      const out = new THREE.Vector3();
      return rayPlaneIntersect(
        raycaster.current.ray.origin,
        raycaster.current.ray.direction,
        ctx.fallbackPlane.point,
        ctx.fallbackPlane.normal,
        out,
      );
    },
    [camera, screenToNDC],
  );

  const isSketchPointer = useCallback((e: PointerEvent): boolean => {
    const pm = useStore.getState().sketchMode;
    if (!pm) return false;
    if (e.pointerType === "pen") return true;
    return (
      useStore.getState().allowNonPenDrawingInput &&
      (e.pointerType === "mouse" || e.pointerType === "touch")
    );
  }, []);

  const updateCursor = useCallback(
    (
      worldPos: THREE.Vector3 | null,
      worldNormal: THREE.Vector3 | null,
      pressure: number,
    ) => {
      if (!worldPos) {
        cursorRef.current.visible = false;
        return;
      }
      cursorRef.current.position.copy(worldPos);
      if (worldNormal) {
        cursorRef.current.normal.copy(worldNormal);
      } else {
        cursorRef.current.normal.copy(camera.getWorldDirection(_tmpDir));
        cursorRef.current.normal.multiplyScalar(-1);
      }
      cursorRef.current.visible = true;
      cursorRef.current.pressure = pressure;
    },
    [camera, cursorRef],
  );

  useEffect(() => {
    const canvas = gl.domElement;
    const prevTouchAction = canvas.style.touchAction;
    canvas.style.touchAction = "none";

    let rafId: number | null = null;
    let pendingMove: SketchPointerInput | null = null;
    let lastInput: SketchPointerInput | null = null;
    let shiftHeld = false;

    const currentTool = () => useStore.getState().sketchMode?.tool ?? "pencil";
    const currentProjection = () =>
      useStore.getState().sketchMode?.projection ?? "projected";

    /**
     * Build the per-frame map of projected-stroke world positions. Used by
     * the eraser to test screen-space proximity against deformed geometry.
     * Skips strokes whose host meshes aren't currently in the scene
     * (orphaned via undo, hidden ancestor, etc.).
     */
    const buildProjectedWorldPositions = (): Map<string, Float32Array> => {
      const out = new Map<string, Float32Array>();
      const sketch = useStore.getState().sketch;
      const meshByObject = buildSketchMeshIndex(scene, isObjectSketchable);
      for (const layer of sketch.layers) {
        if (!layer.visible) continue;
        for (const stroke of layer.strokes) {
          if (!isProjectedStroke(stroke)) continue;
          const buf = new Float32Array(stroke.points.length * 3);
          const positions = projectedStrokeWorldPositions(
            stroke,
            (point) => meshByObject.get(point.attachment.objectId),
            buf,
          );
          if (!positions) continue;
          out.set(stroke.id, buf);
        }
      }
      return out;
    };

    const runErase = (
      input: Pick<SketchPointerInput, "clientX" | "clientY">,
    ) => {
      const pm = useStore.getState().sketchMode;
      if (!pm) return;
      const { rect } = screenToNDC(input.clientX, input.clientY);
      const sketch = useStore.getState().sketch;
      const positions = buildProjectedWorldPositions();
      const hits = findStrokesUnderCursor(
        sketch,
        { x: input.clientX, y: input.clientY },
        camera,
        rect,
        pm.thickness,
        positions,
      );
      if (hits.size > 0) useStore.getState().eraseStrokes(hits);
    };

    const beginProjectedStroke = (input: SketchPointerInput): boolean => {
      const pick = pickProjectedSurface(input.clientX, input.clientY);
      if (!pick) {
        // No surface target — fall through to screenspace fallback so the
        // user can still draw "in air" on a projected-mode session.
        return false;
      }
      const obj = useStore.getState().sceneObjects.get(pick.objectId);
      if (!obj) return false;
      const bindHit = hitToBindPose(pick.hit, pick.mesh, obj);
      if (!bindHit) return false;

      const skin = bakeSkinAttachment(pick.mesh, bindHit.corners, bindHit.bary);
      const pressure = pressureFromInput(input);
      const pm = useStore.getState().sketchMode;
      if (!pm) return false;
      const worldThickness = screenToWorldRadius(
        pm.thickness,
        pick.hit.point,
        camera,
        viewportSize.height,
      );

      // Lock the fallback plane at pointer-down. World-space hit point + a
      // camera-facing normal at this moment. Subsequent samples that miss
      // every surface are projected onto this fixed plane.
      const camDir = new THREE.Vector3();
      camera.getWorldDirection(camDir).normalize();
      const fallbackPlane = {
        point: [pick.hit.point.x, pick.hit.point.y, pick.hit.point.z] as [
          number,
          number,
          number,
        ],
        normal: [-camDir.x, -camDir.y, -camDir.z] as [number, number, number],
      };

      projectedCtx.current = {
        lastSurface: {
          mesh: pick.mesh,
          objectId: pick.objectId,
          topologyVersion: pick.topologyVersion,
        },
        fallbackPlane,
      };

      if (!finitePoint(bindHit.localPos) || !Number.isFinite(pressure)) {
        logSketchWarn("initial projected stroke point has non-finite values", {
          localPos: bindHit.localPos,
          pressure,
          worldThickness,
        });
      }

      const startPoint: SketchStrokePointInput = {
        attachment: {
          objectId: pick.objectId,
          topologyVersion: pick.topologyVersion,
        },
        p: bindHit.localPos,
        pressure,
        skinIndex: skin?.skinIndex,
        skinWeight: skin?.skinWeight,
        corners: skin ? bindHit.corners : undefined,
        bary: skin ? bindHit.bary : undefined,
      };

      useStore.getState().beginStroke({
        ...startPoint,
        worldThickness,
        attachment: {
          objectId: pick.objectId,
          topologyVersion: pick.topologyVersion,
        },
        fallbackPlane,
      });
      lastProjectedWorldPoint.current = [
        pick.hit.point.x,
        pick.hit.point.y,
        pick.hit.point.z,
      ];
      rulerCtx.current = {
        kind: "projected",
        startScreen: { x: input.clientX, y: input.clientY },
        startPoint,
      };

      const normalMatrix = new THREE.Matrix3().getNormalMatrix(
        pick.mesh.matrixWorld,
      );
      const worldNormal = (pick.hit.face?.normal ?? new THREE.Vector3(0, 1, 0))
        .clone()
        .applyMatrix3(normalMatrix)
        .normalize();
      updateCursor(pick.hit.point, worldNormal, pressure);
      cursorRef.current.stroking = true;
      return true;
    };

    const beginScreenspaceStroke = (input: SketchPointerInput): boolean => {
      const pm = useStore.getState().sketchMode;
      if (!pm) return false;
      const p = buildScreenspacePoint(input.clientX, input.clientY);
      const pressure = pressureFromInput(input);
      // Compute world thickness at the screenspace depth.
      const worldPos = new THREE.Vector3(p[0], p[1], p[2]);
      const worldThickness = screenToWorldRadius(
        pm.thickness,
        worldPos,
        camera,
        viewportSize.height,
      );
      const startPoint: SketchStrokePointInput = {
        p,
        pressure,
      };
      useStore.getState().beginStroke({
        ...startPoint,
        worldThickness,
      });
      lastScreenspacePoint.current = p;
      rulerCtx.current = {
        kind: "screenspace",
        startScreen: { x: input.clientX, y: input.clientY },
        startPoint,
      };
      updateCursor(worldPos, null, pressure);
      cursorRef.current.stroking = true;
      return true;
    };

    const captureProjectedPoint = (
      clientX: number,
      clientY: number,
      pressure: number,
      ctx: ProjectedStrokeContext,
    ): CapturedSketchPoint | null => {
      const surface = pickProjectedSurface(clientX, clientY);
      let localPos: [number, number, number] | null = null;
      let attachment: { objectId: string; topologyVersion: number } | null =
        null;
      let bary: [number, number, number] | undefined;
      let corners: [number, number, number] | undefined;
      let skinIndex: [number, number, number, number] | undefined;
      let skinWeight: [number, number, number, number] | undefined;
      let mesh: THREE.Mesh | null = null;
      let worldHit: THREE.Vector3 | null = null;
      let worldNormalForCursor: THREE.Vector3 | null = null;

      if (surface) {
        const obj = useStore.getState().sceneObjects.get(surface.objectId);
        if (!obj) return null;
        const bindHit = hitToBindPose(surface.hit, surface.mesh, obj);
        if (bindHit) {
          localPos = bindHit.localPos;
          attachment = {
            objectId: surface.objectId,
            topologyVersion: surface.topologyVersion,
          };
          bary = bindHit.bary;
          corners = bindHit.corners;
          const skin = bakeSkinAttachment(
            surface.mesh,
            bindHit.corners,
            bindHit.bary,
          );
          skinIndex = skin?.skinIndex;
          skinWeight = skin?.skinWeight;
          mesh = surface.mesh;
          worldHit = surface.hit.point;
          if (surface.hit.face) {
            const normalMatrix = new THREE.Matrix3().getNormalMatrix(
              surface.mesh.matrixWorld,
            );
            worldNormalForCursor = surface.hit.face.normal
              .clone()
              .applyMatrix3(normalMatrix)
              .normalize();
          }
          ctx.lastSurface = {
            mesh: surface.mesh,
            objectId: surface.objectId,
            topologyVersion: surface.topologyVersion,
          };
        }
      }

      // If raycast missed every surface, fall back to the locked plane and
      // keep using the most recent surface's local coordinate frame.
      if (!localPos) {
        const planeHit = projectOntoFallbackPlane(clientX, clientY, ctx);
        if (!planeHit) return null;
        const localPlaneHit = ctx.lastSurface.mesh.worldToLocal(
          planeHit.clone(),
        );
        localPos = [localPlaneHit.x, localPlaneHit.y, localPlaneHit.z];
        attachment = {
          objectId: ctx.lastSurface.objectId,
          topologyVersion: ctx.lastSurface.topologyVersion,
        };
        mesh = ctx.lastSurface.mesh;
        worldHit = planeHit;
      }

      if (!attachment) return null;
      return {
        attachment,
        p: localPos,
        pressure,
        skinIndex,
        skinWeight,
        corners,
        bary,
        mesh,
        worldHit,
        worldNormal: worldNormalForCursor,
      };
    };

    const continueProjectedStroke = (input: SketchPointerInput) => {
      const ctx = projectedCtx.current;
      if (!ctx) return;
      const pm = useStore.getState().sketchMode;
      if (!pm) return;

      const pressure = pressureFromInput(input);
      const sample = captureProjectedPoint(
        input.clientX,
        input.clientY,
        pressure,
        ctx,
      );
      if (!sample) return;

      const localRadius =
        sample.worldHit && sample.mesh
          ? screenToObjectLocalRadius(
              pm.thickness,
              sample.worldHit,
              sample.mesh.matrixWorld,
              camera,
              viewportSize.height,
            )
          : 0;
      const minSpacing = Math.max(
        1e-6,
        localRadius * MIN_POINT_SPACING_FRACTION,
      );
      const worldStep =
        lastProjectedWorldPoint.current && sample.worldHit
          ? distance3(lastProjectedWorldPoint.current, [
              sample.worldHit.x,
              sample.worldHit.y,
              sample.worldHit.z,
            ])
          : Number.POSITIVE_INFINITY;
      if (lastProjectedWorldPoint.current && worldStep < minSpacing) {
        if (sample.worldHit) {
          updateCursor(sample.worldHit, sample.worldNormal, pressure);
        }
        return;
      }

      useStore.getState().addStrokePoint({
        attachment: sample.attachment,
        p: sample.p,
        pressure: sample.pressure,
        skinIndex: sample.skinIndex,
        skinWeight: sample.skinWeight,
        corners: sample.corners,
        bary: sample.bary,
      });
      if (sample.worldHit) {
        lastProjectedWorldPoint.current = [
          sample.worldHit.x,
          sample.worldHit.y,
          sample.worldHit.z,
        ];
        updateCursor(sample.worldHit, sample.worldNormal, pressure);
      }
    };

    const captureScreenspacePoint = (
      clientX: number,
      clientY: number,
      pressure: number,
    ): CapturedSketchPoint => {
      const p = buildScreenspacePoint(clientX, clientY);
      return {
        p,
        pressure,
        mesh: null,
        worldHit: new THREE.Vector3(p[0], p[1], p[2]),
        worldNormal: null,
      };
    };

    const continueScreenspaceStroke = (input: SketchPointerInput) => {
      const pm = useStore.getState().sketchMode;
      if (!pm) return;
      const pressure = pressureFromInput(input);
      const sample = captureScreenspacePoint(
        input.clientX,
        input.clientY,
        pressure,
      );
      const last = lastScreenspacePoint.current;
      if (last) {
        const step = distance3(last, sample.p);
        if (step < SCREENSPACE_MIN_SPACING) return;
      }
      useStore.getState().addStrokePoint({
        p: sample.p,
        pressure: sample.pressure,
      });
      lastScreenspacePoint.current = sample.p;
      updateCursor(sample.worldHit, null, pressure);
    };

    const updateRulerStroke = (input: SketchPointerInput, snap45: boolean) => {
      const ruler = rulerCtx.current;
      if (!ruler) return;
      const pm = useStore.getState().sketchMode;
      if (!pm) return;
      const projectedLineCtx =
        ruler.kind === "projected" ? projectedCtx.current : null;
      const rawEnd = { x: input.clientX, y: input.clientY };
      const end = snap45
        ? snapScreenPointTo45(ruler.startScreen, rawEnd)
        : rawEnd;
      const samples = sampleScreenLine(
        ruler.startScreen,
        end,
        LINE_SAMPLE_SPACING_PX,
        LINE_MAX_SEGMENTS,
      );
      const endPressure = pressureFromInput(input);
      const points: SketchStrokePointInput[] = [];
      let lastCaptured: CapturedSketchPoint | null = null;

      for (const sample of samples) {
        const pressure =
          ruler.startPoint.pressure +
          (endPressure - ruler.startPoint.pressure) * sample.t;
        if (sample.t === 0) {
          points.push({ ...ruler.startPoint, pressure });
          continue;
        }

        const captured =
          ruler.kind === "projected"
            ? projectedLineCtx
                ? captureProjectedPoint(
                    sample.x,
                    sample.y,
                    pressure,
                    projectedLineCtx,
                  )
                : null
            : captureScreenspacePoint(sample.x, sample.y, pressure);
        if (!captured) continue;
        points.push({
          attachment: captured.attachment,
          p: captured.p,
          pressure: captured.pressure,
          skinIndex: captured.skinIndex,
          skinWeight: captured.skinWeight,
          corners: captured.corners,
          bary: captured.bary,
        });
        lastCaptured = captured;
      }

      if (points.length === 0) points.push(ruler.startPoint);
      useStore.getState().replaceInFlightStrokePoints(points);

      const lastPoint = points[points.length - 1];
      if (ruler.kind === "projected") {
        lastProjectedWorldPoint.current = lastCaptured?.worldHit
          ? [
              lastCaptured.worldHit.x,
              lastCaptured.worldHit.y,
              lastCaptured.worldHit.z,
            ]
          : null;
      } else {
        lastScreenspacePoint.current = lastPoint.p;
      }

      if (lastCaptured?.worldHit) {
        updateCursor(
          lastCaptured.worldHit,
          lastCaptured.worldNormal,
          lastCaptured.pressure,
        );
      }
    };

    const onPointerDown = (e: PointerEvent) => {
      if (!isSketchPointer(e)) return;
      if (e.button !== 0) return;
      const pm = useStore.getState().sketchMode;
      if (!pm) return;
      const input = eventToSketchInput(e);
      lastInput = input;
      shiftHeld = input.shiftKey;

      logSketchDebug("pointer down", {
        pointerId: e.pointerId,
        pointerType: e.pointerType,
        layerId: pm.activeLayerId,
        tool: pm.tool,
        projection: pm.projection,
        thickness: pm.thickness,
      });

      // Erase mode is shared between projection modes — eraser reads the
      // entire scene sketch.
      if (currentTool() === "erase") {
        useStore.getState().pushUndo();
        strokeActive.current = true;
        rulerCtx.current = null;
        rulerActive.current = false;
        canvas.setPointerCapture(e.pointerId);
        e.preventDefault();
        runErase(input);
        cursorRef.current.stroking = true;
        cursorRef.current.pressure = 1;
        return;
      }

      useStore.getState().pushUndo();
      strokeActive.current = true;
      rulerCtx.current = null;
      rulerActive.current = input.shiftKey;
      canvas.setPointerCapture(e.pointerId);
      e.preventDefault();

      let started = false;
      if (currentProjection() === "projected") {
        started = beginProjectedStroke(input);
      }
      if (!started) {
        // Either projection is "screenspace" or projected mode found no
        // surface target. In both cases we fall through to screenspace.
        started = beginScreenspaceStroke(input);
      }
      if (!started) {
        // Should not happen — beginScreenspaceStroke only fails if sketchMode
        // disappeared between branches. Reset state defensively.
        strokeActive.current = false;
        rulerCtx.current = null;
        rulerActive.current = false;
        canvas.releasePointerCapture(e.pointerId);
      }
    };

    const onPointerMoveImpl = (input: SketchPointerInput) => {
      lastInput = input;
      shiftHeld = input.shiftKey;
      const pm = useStore.getState().sketchMode;
      if (!pm) {
        cursorRef.current.visible = false;
        return;
      }

      if (strokeActive.current && currentTool() === "erase") {
        runErase(input);
        // Cursor preview during erase tracks the cursor along the camera ray.
        const p = buildScreenspacePoint(input.clientX, input.clientY);
        updateCursor(new THREE.Vector3(p[0], p[1], p[2]), null, 1);
        return;
      }

      if (!strokeActive.current) {
        // Hover preview only — projected mode shows the tool on whatever
        // mesh is under the cursor; screenspace shows it 1u out.
        if (currentProjection() === "projected") {
          const pick = pickProjectedSurface(input.clientX, input.clientY);
          if (pick) {
            const normalMatrix = new THREE.Matrix3().getNormalMatrix(
              pick.mesh.matrixWorld,
            );
            const wn = (pick.hit.face?.normal ?? new THREE.Vector3(0, 1, 0))
              .clone()
              .applyMatrix3(normalMatrix)
              .normalize();
            updateCursor(pick.hit.point, wn, 0);
          } else {
            const p = buildScreenspacePoint(input.clientX, input.clientY);
            updateCursor(new THREE.Vector3(p[0], p[1], p[2]), null, 0);
          }
        } else {
          const p = buildScreenspacePoint(input.clientX, input.clientY);
          updateCursor(new THREE.Vector3(p[0], p[1], p[2]), null, 0);
        }
        cursorRef.current.stroking = false;
        return;
      }

      if (rulerActive.current || input.shiftKey) {
        rulerActive.current = true;
        updateRulerStroke(input, input.shiftKey);
        return;
      }

      if (projectedCtx.current) {
        continueProjectedStroke(input);
      } else {
        continueScreenspaceStroke(input);
      }
    };

    const onPointerMoveCoalesced = (e: PointerEvent) => {
      if (!isSketchPointer(e)) return;
      pendingMove = eventToSketchInput(e);
      if (rafId == null) {
        rafId = requestAnimationFrame(() => {
          rafId = null;
          if (pendingMove) {
            onPointerMoveImpl(pendingMove);
            pendingMove = null;
          }
        });
      }
    };

    const onPointerUp = (e: PointerEvent) => {
      if (!strokeActive.current) return;
      const input = eventToSketchInput(e);
      if (rulerActive.current && currentTool() !== "erase") {
        updateRulerStroke(input, input.shiftKey);
      }
      if (rafId != null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      pendingMove = null;
      strokeActive.current = false;
      lastProjectedWorldPoint.current = null;
      lastScreenspacePoint.current = null;
      projectedCtx.current = null;
      rulerCtx.current = null;
      rulerActive.current = false;
      lastInput = null;
      shiftHeld = false;
      cursorRef.current.stroking = false;
      useStore.getState().endSketchStroke();
      canvas.releasePointerCapture(e.pointerId);
    };

    const onPointerLeave = () => {
      cursorRef.current.visible = false;
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Shift" || shiftHeld) return;
      shiftHeld = true;
      if (
        strokeActive.current &&
        currentTool() !== "erase" &&
        lastInput &&
        rulerCtx.current
      ) {
        rulerActive.current = true;
        updateRulerStroke({ ...lastInput, shiftKey: true }, true);
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key !== "Shift" || !shiftHeld) return;
      shiftHeld = false;
      if (
        strokeActive.current &&
        rulerActive.current &&
        currentTool() !== "erase" &&
        lastInput
      ) {
        updateRulerStroke({ ...lastInput, shiftKey: false }, false);
      }
    };

    const onWindowBlur = () => {
      shiftHeld = false;
    };

    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointermove", onPointerMoveCoalesced);
    canvas.addEventListener("pointerup", onPointerUp);
    canvas.addEventListener("pointerleave", onPointerLeave);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onWindowBlur);

    return () => {
      canvas.style.touchAction = prevTouchAction;
      if (rafId != null) cancelAnimationFrame(rafId);
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMoveCoalesced);
      canvas.removeEventListener("pointerup", onPointerUp);
      canvas.removeEventListener("pointerleave", onPointerLeave);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onWindowBlur);
    };
  }, [
    gl,
    camera,
    viewportSize,
    pickProjectedSurface,
    buildScreenspacePoint,
    projectOntoFallbackPlane,
    screenToNDC,
    isSketchPointer,
    updateCursor,
    cursorRef,
    scene,
  ]);

  return null;
}

const _tmpDir = new THREE.Vector3();
